/**
 * Internal project tags: the chips on the drawer header, and the picker behind the plus.
 *
 * STAFF ONLY. The server never sends `tags` to a client or an editor, and this renders nothing
 * without them, so there is no path by which a client sees the team's note about their own job.
 *
 * WHY A PICKER AND NOT A TEXT BOX. The list is the whole point: the fourth person who means
 * "unhappy client" should get the first person's tag instead of inventing a fourth spelling. So
 * existing tags come first, most used at the top, and typing only offers to create once nothing in
 * the list matches. The server normalises anyway (case, spacing, dashes, quotes), so a variant
 * typed here resolves to the tag that exists rather than a twin; the list is what stops people
 * needing to rely on that.
 *
 * A tag carries an optional one-line why, which is where the detail goes. The chip stays short
 * enough to read on a row.
 */
import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Plus, X, Tag as TagIcon, Eye, EyeOff, Check } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { formatEastern } from '@/lib/format';
import { TAG_COLORS, tagChipStyle, tagInk, DEFAULT_TAG_COLOR } from '@/lib/tagColors';

// `tap-auto` on EVERY interactive element here. index.css puts a 44px floor under every <button>
// for touch targets, and a chip is not a touch target: the remove X inside one was missing the
// opt-out and inflated the whole pill to 44px tall, which is the "way too big" the operator saw.
const CHIP = 'tap-auto inline-flex items-center gap-1 font-sans text-[9px] font-semibold uppercase tracking-wider border rounded-full px-2 py-0.5 transition-colors';

export function ProjectTags({ project, canEdit, isAdmin, onChange }) {
  const tags = project?.tags;
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(null);
  const [typed, setTyped] = useState('');
  const [note, setNote] = useState('');
  const [color, setColor] = useState(DEFAULT_TAG_COLOR);
  const [saving, setSaving] = useState(false);

  const loadList = useCallback(async () => {
    try {
      const r = await api.get('/project-tags');
      setList(r.data.tags || []);
    } catch { setList([]); }
  }, []);

  useEffect(() => { if (open && list === null) loadList(); }, [open, list, loadList]);

  // TWO gates, not one. The server does not send `tags` to anyone outside the team, and this
  // refuses to draw them even if it is handed some: presence-only was the reason a payload leak
  // from PATCH /projects/{id}/status put the staff chips in an EDITOR's drawer (audit, 2026-09-18).
  // A component that renders whatever it is given makes every backend slip a visible one.
  if (!canEdit || !Array.isArray(tags)) return null;

  const on = new Set(tags.map(t => t.tag_id));
  const typedTrim = typed.trim();
  // Same comparison the server keys on, so "Unhappy Client" does not look like a new tag here.
  const norm = (s) => (s || '').normalize('NFKC').replace(/[‘’‛′´`]/g, "'")
    .toLowerCase().replace(/[\s_‐-―-]+/g, ' ').replace(/^[^0-9a-z]+|[^0-9a-z]+$/g, '').trim();
  // Tags ALREADY on this project stay in the list, marked, rather than disappearing from it. Two
  // reasons, both found by using it: "No tags yet" is a lie when the only tag is the one already
  // applied, and the admin's watched switch lives on these rows, so hiding an applied tag hid the
  // switch for exactly the tag most likely to want it.
  const matches = (list || []).filter(t => !typedTrim || norm(t.label).includes(norm(typedTrim)));
  const exact = (list || []).find(t => norm(t.label) === norm(typedTrim));

  const apply = async (body) => {
    setSaving(true);
    try {
      await api.post(`/projects/${project.id}/tags`, body);
      setTyped(''); setNote(''); setColor(DEFAULT_TAG_COLOR); setOpen(false); setList(null);
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add that tag'); }
    finally { setSaving(false); }
  };

  // ADMIN ONLY. Creating a tag is open to staff; deciding that one belongs in the daily check is
  // not, so this is the only place `watched` is set and it is gated on the server as well.
  const setWatched = async (tag) => {
    try {
      await api.put(`/project-tags/${tag.id}`, { watched: !tag.watched });
      toast.success(!tag.watched ? `${tag.label} now shows in Needs Attention` : `${tag.label} removed from Needs Attention`);
      setList(null); loadList();
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not change that tag'); }
  };

  const remove = async (tagId, label) => {
    try {
      await api.delete(`/projects/${project.id}/tags/${tagId}`);
      toast.success(`${label} removed`);
      setList(null);
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not remove that tag'); }
  };

  return (
    <>
      {tags.map(t => (
        <span key={t.tag_id} data-testid={`project-tag-${t.tag_id}`}
          title={[t.note, t.added_by_name ? `added by ${t.added_by_name}${t.added_at ? ` on ${formatEastern(t.added_at)}` : ''}` : '',
            t.retired ? 'This tag has been retired; it stays on the jobs that already carry it.' : '']
            .filter(Boolean).join(' · ')}
          style={tagChipStyle(t.color)} className={CHIP}>
          {t.label}
          {canEdit && (
            <button onClick={() => remove(t.tag_id, t.label)} title={`Remove ${t.label}`}
              data-testid={`project-tag-remove-${t.tag_id}`}
              className="tap-auto opacity-60 hover:opacity-100 transition-opacity">
              <X className="w-2.5 h-2.5" />
            </button>
          )}
        </span>
      ))}

      {canEdit && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button data-testid="project-tag-add" title="Tag this project for the team"
              className={`${CHIP} text-slate-500 hover:text-ug-gold border-ug-border hover:border-gold-leaf/30`}>
              <Plus className="w-2.5 h-2.5" /> Tag
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-72 p-0 bg-ug-raised border-ug-border" data-testid="project-tag-picker">
            <div className="p-2 border-b border-ug-border">
              <Input autoFocus value={typed} onChange={e => setTyped(e.target.value)} placeholder="Find a tag, or type a new one"
                data-testid="project-tag-search"
                className="h-7 text-[11px] bg-ug-surface border-ug-border text-zinc-100 placeholder:text-slate-600" />
            </div>
            <div className="max-h-52 overflow-y-auto py-1">
              {list === null && <p className="px-3 py-2 text-[10px] text-slate-600">Loading tags…</p>}
              {list !== null && matches.length === 0 && !typedTrim && (
                <p className="px-3 py-2 text-[10px] text-slate-600">No tags yet. Type one and it joins the list for everyone.</p>
              )}
              {list !== null && matches.length === 0 && typedTrim && (
                <p className="px-3 py-2 text-[10px] text-slate-600">Nothing matches “{typedTrim}”.</p>
              )}
              {matches.map(t => (
                <div key={t.id} className="flex items-center hover:bg-ug-surface transition-colors">
                  <button onClick={() => apply({ tag_id: t.id, note })} disabled={saving || on.has(t.id)}
                    data-testid={`project-tag-option-${t.id}`}
                    title={on.has(t.id) ? `${t.label} is already on this project` : `Tag this project ${t.label}`}
                    className="tap-auto flex-1 min-w-0 text-left pl-3 pr-2 py-1.5 flex items-center justify-between gap-2 disabled:opacity-40 disabled:cursor-default">
                    <span className="flex items-center gap-1.5 min-w-0">
                      {on.has(t.id)
                        ? <Check className="w-2.5 h-2.5 shrink-0 text-oxidized-teal" />
                        : <TagIcon className="w-2.5 h-2.5 shrink-0" style={{ color: tagInk(t.color) }} />}
                      <span className="text-[11px] text-zinc-100 truncate">{t.label}</span>
                    </span>
                    <span className="font-mono text-[9px] text-slate-600 shrink-0">
                      {on.has(t.id) ? 'on' : t.times_used > 0 ? `${t.times_used}` : 'unused'}
                    </span>
                  </button>
                  {isAdmin && (
                    <button onClick={() => setWatched(t)} data-testid={`project-tag-watch-${t.id}`}
                      title={t.watched
                        ? `${t.label} is in the daily Needs Attention check. Click to take it out.`
                        : `Put ${t.label} in the daily Needs Attention check.`}
                      className={`tap-auto shrink-0 px-2 py-1.5 transition-colors ${t.watched ? 'text-ug-status-red hover:text-ug-status-red/70' : 'text-slate-600 hover:text-ug-gold'}`}>
                      {t.watched ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                    </button>
                  )}
                </div>
              ))}
              {typedTrim && !exact && (
                <div className="border-t border-ug-border/60">
                  <button onClick={() => apply({ label: typedTrim, note, color })} disabled={saving}
                    data-testid="project-tag-create"
                    className="tap-auto w-full text-left px-3 py-1.5 hover:bg-ug-surface transition-colors disabled:opacity-50">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: tagInk(color) }} />
                      <span className="text-[11px] text-ug-gold">Create “{typedTrim}”</span>
                    </span>
                    <span className="block text-[9px] text-slate-600">It joins the list for everyone next time.</span>
                  </button>
                  {/* Colour is chosen once, when the tag is born. Changing it later is an admin job
                      on /admin/project-tags, because the colour belongs to the tag and not to this
                      project's copy of it. */}
                  <div className="flex items-center gap-1.5 px-3 pb-2" data-testid="project-tag-colors">
                    {TAG_COLORS.map(c => (
                      <button key={c.key} onClick={() => setColor(c.key)} title={c.label}
                        data-testid={`project-tag-color-${c.key}`}
                        className={`tap-auto w-4 h-4 rounded-full border transition-transform ${color === c.key ? 'scale-125 border-current' : 'border-transparent hover:scale-110'}`}
                        style={{ backgroundColor: tagInk(c.key), color: tagInk(c.key) }} />
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="p-2 border-t border-ug-border">
              <Input value={note} onChange={e => setNote(e.target.value)} maxLength={140}
                placeholder="Why, in a line (optional)" data-testid="project-tag-note"
                className="h-7 text-[11px] bg-ug-surface border-ug-border text-zinc-100 placeholder:text-slate-600" />
            </div>
          </PopoverContent>
        </Popover>
      )}
    </>
  );
}
